import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Check,
  ImageIcon,
  LoaderCircle,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import { api } from "../api";
import type { ProductImageRegistry } from "../types";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("读取商品图片失败"));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("商品图片内容格式无效"));
        return;
      }
      const separator = reader.result.indexOf(",");
      if (separator < 0) {
        reject(new Error("商品图片编码失败"));
        return;
      }
      resolve(reader.result.slice(separator + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function ProductImageManager({
  productId,
  registry,
  onUpdated,
}: {
  productId: string;
  registry: ProductImageRegistry;
  onUpdated: () => Promise<unknown>;
}) {
  const [message, setMessage] = useState(
    "支持 PNG、JPEG、WebP，单张不超过 10MB。新上传图片会自动设为当前主参考图。",
  );
  const configQuery = useQuery({ queryKey: ["config"], queryFn: api.getConfig });
  const imageUploadEnabled =
    configQuery.data?.capabilities.imageUpload ?? true;

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_IMAGE_BYTES) {
        throw new Error("商品图片不能超过 10MB");
      }
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        throw new Error("仅支持 PNG、JPEG 或 WebP 商品图片");
      }
      const contentBase64 = await readFileAsBase64(file);
      return api.uploadProductImage(productId, file.name, contentBase64);
    },
    onSuccess: async () => {
      setMessage("上传成功，已设为当前主参考图。");
      await onUpdated();
    },
    onError: (error: Error) => setMessage(error.message),
  });

  const selectMutation = useMutation({
    mutationFn: (assetId: string) =>
      api.selectProductImage(productId, assetId),
    onSuccess: async () => {
      setMessage("主参考图已切换。");
      await onUpdated();
    },
    onError: (error: Error) => setMessage(error.message),
  });

  const isBusy = uploadMutation.isPending || selectMutation.isPending;
  const selected = registry.assets.find((asset) => asset.is_selected);

  return (
    <section className="product-image-manager" aria-labelledby="product-image-title">
      <header>
        <div>
          <span className="document-label">PRODUCT REFERENCE</span>
          <h3 id="product-image-title">商品主参考图</h3>
          <p>
            当前生成链路会优先使用这里选中的图片，确保多镜头中的商品外观更一致。
          </p>
        </div>
        <label className="product-image-upload" aria-disabled={!imageUploadEnabled}>
          {uploadMutation.isPending ? (
            <LoaderCircle className="is-spinning" size={17} aria-hidden="true" />
          ) : (
            <UploadCloud size={17} aria-hidden="true" />
          )}
          {imageUploadEnabled ? "上传 / 替换真实商品图" : "公开 Demo 禁止图片上传"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={isBusy || !imageUploadEnabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              event.target.value = "";
            }}
          />
        </label>
      </header>

      <div className="product-image-layout">
        <div className="product-image-primary">
          {selected ? (
            <img
              src={selected.preview_url}
              alt={`${productId} 当前商品主参考图`}
            />
          ) : (
            <div className="product-image-empty">
              <ImageIcon size={28} aria-hidden="true" />
              <strong>尚未添加商品图片</strong>
              <span>请上传一张正面清晰、主体完整的真实商品图。</span>
            </div>
          )}
          {selected && (
            <div className="product-image-caption">
              <strong>{selected.source_label}</strong>
              <span>
                {selected.source === "ai-demo"
                  ? "仅用于流程演示，正式业务请替换为真实商品实拍"
                  : "用户上传 · 当前已选"}
              </span>
            </div>
          )}
        </div>

        <div className="product-image-rail">
          {registry.assets.length === 0 ? (
            <p className="product-image-hint">暂无可选图片</p>
          ) : (
            registry.assets.map((asset) => (
              <button
                type="button"
                key={asset.asset_id}
                className={
                  asset.is_selected
                    ? "product-image-thumb is-selected"
                    : "product-image-thumb"
                }
                disabled={isBusy || asset.is_selected}
                onClick={() => selectMutation.mutate(asset.asset_id)}
              >
                <img src={asset.preview_url} alt={asset.source_label} />
                <span>
                  {asset.source === "ai-demo" ? "AI 演示图" : "真实商品图"}
                </span>
                {asset.is_selected && (
                  <Check size={14} aria-label="当前主图" />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <footer>
        <p
          className={
            uploadMutation.isError || selectMutation.isError
              ? "product-image-message is-error"
              : "product-image-message"
          }
        >
          {message}
        </p>
        <p className="product-image-security">
          <ShieldCheck size={14} aria-hidden="true" />
          {imageUploadEnabled
            ? "安全预览只读取本地文件；正式付费生成确认后，才上传至私有 TOS 并生成临时签名 URL。"
            : "公开 Demo 只使用种子参考图，不读取访客文件，也不连接外部存储。"}
        </p>
      </footer>
    </section>
  );
}
